import { useState } from 'react';
import { Box, Button, Collapse, List, ListItemButton, Stack, Typography } from '@mui/material';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import { CollapseIcon, ExpandIcon } from '../../utils/icons';
import { TestIds } from '../../constants';
import { useFavorites } from '../../contexts/FavoritesContext';
import { FAVORITES_CAP } from '../../services/favoritesStorage';
import { FavoriteRow } from './FavoriteRow';
import { FavoritesEmptyState } from './FavoritesEmptyState';

interface FavoritesSectionProps {
  /** Called after navigating to a favorite, so the host can close the drawer. */
  onNavigate?: () => void;
}

/**
 * Always-present sidebar section listing pinned pages as flat links. Rendered directly by the
 * sidebar (not via the registry-driven navigation config) since favorites are user data, but
 * the rows themselves intentionally behave like ordinary nav items. All management — add,
 * rename, remove, reorder — lives in the Favorites Manager, opened via the "Manage" button.
 */
export function FavoritesSection({ onNavigate }: FavoritesSectionProps) {
  const { favorites, openManager } = useFavorites();
  const [isExpanded, setIsExpanded] = useState(true);
  const currentPageAlreadySaved = favorites.some((f) => f.pathname === window.location.pathname);

  return (
    <Box data-testid={TestIds.FAVORITES_SECTION}>
      <Stack
        direction="row"
        alignItems="center"
        data-testid={TestIds.FAVORITES_SECTION_HEADER}
        sx={{ pl: 1, pr: 1, minHeight: 32 }}
      >
        <ListItemButton
          onClick={() => setIsExpanded((prev) => !prev)}
          disableRipple
          aria-expanded={isExpanded}
          sx={{ flexGrow: 1, px: 1, py: 0.5, '&:hover': { backgroundColor: 'transparent' } }}
        >
          <Stack direction="row" alignItems="center" gap={0.5}>
            {isExpanded ? (
              <CollapseIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            ) : (
              <ExpandIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            )}
            <Typography
              variant="subtitle2"
              component="h2"
              sx={{ color: `${capitalDesignTokens.foundations.colors.gray700}` }}
            >
              Favorites
            </Typography>
          </Stack>
        </ListItemButton>
        {favorites.length > 0 && (
          <Button
            variant="text"
            color="primary"
            size="small"
            data-testid={TestIds.FAVORITES_MANAGE_BUTTON}
            onClick={openManager}
          >
            Manage
          </Button>
        )}
      </Stack>

      <Collapse in={isExpanded} timeout="auto">
        {favorites.length > 0 && (
          <List disablePadding>
            {favorites.map((favorite) => (
              <FavoriteRow key={favorite.pathname} favorite={favorite} onNavigate={onNavigate} />
            ))}
          </List>
        )}
        <FavoritesEmptyState
          disabled={favorites.length >= FAVORITES_CAP || currentPageAlreadySaved}
        />
      </Collapse>
    </Box>
  );
}
